/** Word/Docs/Firestore UI often paste “smart” quotes — invalid as JSON string delimiters. */
function relaxInvalidJsonQuotes(s) {
  return s
    .replace(/\u201C/g, '"')
    .replace(/\u201D/g, '"')
    .replace(/\u2018/g, "'")
    .replace(/\u2019/g, "'");
}

/**
 * Try JSON.parse on raw and on quote-normalized text; handle double-encoded API stubs.
 * @param {string} raw
 * @returns {Record<string, unknown> | null}
 */
function tryParseDecisionJsonString(raw) {
  const outer = [raw, relaxInvalidJsonQuotes(raw)];
  for (const cand of outer) {
    try {
      let v = JSON.parse(cand);
      if (typeof v === "string") {
        const inner = v.trim();
        if (!inner) return null;
        const innerCandidates = [inner, relaxInvalidJsonQuotes(inner)];
        let next = null;
        for (const ic of innerCandidates) {
          try {
            next = JSON.parse(ic);
            break;
          } catch {
            /* try next */
          }
        }
        if (next == null) return null;
        v = next;
      }
      if (typeof v === "object" && v !== null && !Array.isArray(v)) return v;
    } catch {
      /* try next outer candidate */
    }
  }
  return null;
}

/**
 * API may return a plain object (Firestore map / legacy) or a JSON *string* (opaque pasted text).
 * Stub responses may double-encode (JSON string of JSON text) — unwrap until we get an object.
 * @param {unknown} data
 * @returns {Record<string, unknown> | null}
 */
export function parseDecisionOutputPayload(data) {
  if (data == null) return null;
  if (typeof data === "object" && !Array.isArray(data)) {
    return data;
  }
  if (typeof data !== "string") return null;

  const s = data.trim().replace(/^\uFEFF/, "");
  if (!s) return null;

  const parsed = tryParseDecisionJsonString(s);
  if (parsed == null) {
    // eslint-disable-next-line no-console
    console.warn("[parseDecisionOutputPayload] could not parse JSON (check for stray smart quotes or commas)");
  }
  return parsed;
}

/** @param {string} key */
export function humanizeSnakeCase(key) {
  if (key == null || typeof key !== "string") return "";
  const trimmed = key.trim();
  if (!trimmed) return "";
  return trimmed
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const HEADER_OVERRIDES = {
  name: "Name",
  train: "Train",
  validation: "Validation",
  baseline: "Baseline",
  before_filter: "Before filter",
  after_filter: "After Filtering",
  improvement: "Improvement",
};

/** @param {string} key */
export function humanizeColumnHeader(key) {
  if (key == null || typeof key !== "string") return "";
  const lower = key.toLowerCase();
  if (HEADER_OVERRIDES[lower] != null) return HEADER_OVERRIDES[lower];
  return humanizeSnakeCase(key);
}

/**
 * @param {number} n
 * @param {"percentage"|"usd"|string|undefined} unit
 */
export function formatValueWithUnit(n, unit) {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n);
  const u = (unit || "").toLowerCase();
  if (u === "percentage") return `${(n * 100).toFixed(2)}%`;
  if (u === "usd") return formatUsd(n);
  return formatNumber(n);
}

/**
 * @param {number} n
 * @param {number} [fractionDigits=2]
 */
export function formatUsd(n, fractionDigits = 2) {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n);
  const fd = fractionDigits;
  const sign = n < 0 ? "-" : "";
  const v = Math.abs(n);
  if (v >= 1e9) return `${sign}$${(v / 1e9).toFixed(fd)}B`;
  if (v >= 1e6) return `${sign}$${(v / 1e6).toFixed(fd)}M`;
  if (v >= 1e3) return `${sign}$${(v / 1e3).toFixed(fd)}K`;
  return `${sign}$${v.toFixed(fd)}`;
}

export function formatNumber(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return String(n);
  if (Number.isInteger(n)) return String(n);
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

/** e.g. 0.2 -> "20% filter" */
export function formatThreshold(t) {
  if (typeof t !== "number" || !Number.isFinite(t)) return String(t);
  const pct = t <= 1 && t >= 0 ? t * 100 : t;
  const rounded = Math.round(pct * 100) / 100;
  return `${rounded}% filter`;
}

/**
 * Renders `improvement` / `improvement_sign` + `improvement_value` from backend JSON (no baseline/after math).
 * Prefer a non-empty `improvement` string; otherwise format sign + value by `unit`.
 * @param {Record<string, unknown>} row
 */
export function formatDecisionImprovementDisplay(row) {
  if (row == null || typeof row !== "object") return "—";
  if (typeof row.improvement === "string" && row.improvement.trim()) return row.improvement.trim();
  const sign = row.improvement_sign == null ? "" : String(row.improvement_sign);
  const val = row.improvement_value;
  if (typeof val !== "number" || !Number.isFinite(val)) return "—";
  const u = (typeof row.unit === "string" ? row.unit : "").toLowerCase();
  if (u === "percentage") {
    const pct = Math.abs(val) <= 1 ? val * 100 : val;
    return `${sign}${pct.toFixed(1)}%`;
  }
  if (u === "usd") {
    if (Math.abs(val) <= 1) {
      return `${sign}${(val * 100).toFixed(1)}%`;
    }
    return `${sign}${formatUsd(val, 1)}`;
  }
  return `${sign}${val.toFixed(1)}`;
}
